import { useState, useRef, useEffect, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { EditorialHeading } from "./Reveal";

const CASES = [
  { before: "/ba/masonry-before.jpg", after: "/ba/masonry-after.jpg" },
  { before: "/ba/crown-before.jpg", after: "/ba/crown-after.jpg" },
  { before: "/ba/fireplace-before.jpg", after: "/ba/fireplace-after.jpg" },
];

const Slider = ({ pair, beforeLabel, afterLabel, dragLabel, caption, index }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const interacted = useRef(false);
  const inView = useInView(containerRef, { once: true, margin: "-25%" });

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 2200;
    let raf;
    const tick = (now) => {
      if (interacted.current) return;
      const p = Math.min((now - start) / dur, 1);
      setPos(50 + Math.sin(p * Math.PI * 2) * 24 * (1 - p * 0.35));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setPos(50);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  const updateFromClientX = useCallback((clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  }, []);

  const onPointerMove = (e) => {
    if (e.buttons !== 1 && e.pointerType === "mouse") return;
    interacted.current = true;
    updateFromClientX(e.clientX);
  };

  return (
    <motion.figure
      data-testid={`before-after-slider-${index}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        ref={containerRef}
        className="ba-handle relative aspect-[3/2] rounded-2xl overflow-hidden border border-[#e0dcd4] bg-neutral-200 shadow-xl shadow-neutral-300/50 select-none"
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); interacted.current = true; updateFromClientX(e.clientX); }}
        onPointerMove={onPointerMove}
      >
        <img src={pair.after} alt={`${caption} — ${afterLabel}`} draggable={false} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <img src={pair.before} alt={`${caption} — ${beforeLabel}`} draggable={false} className="w-full h-full object-cover" />
        </div>

        <div className="absolute top-0 bottom-0 w-[2px] bg-white/90 shadow-[0_0_14px_rgba(0,0,0,0.45)]" style={{ left: `${pos}%` }}>
          <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-xl flex items-center justify-center">
            <MoveHorizontal size={16} className="text-[#ff6b00]" />
          </span>
        </div>

        <span className="absolute top-4 left-4 rounded-full bg-neutral-900/75 backdrop-blur px-3 py-1 font-tech text-[10px] tracking-[0.25em] text-white">{beforeLabel}</span>
        <span className="absolute top-4 right-4 rounded-full bg-[#ff6b00] px-3 py-1 font-tech text-[10px] tracking-[0.25em] text-white">{afterLabel}</span>
      </div>

      <figcaption className="mt-4 flex items-baseline gap-3 px-1">
        <span className="font-display italic text-3xl sm:text-4xl text-[#d8d3ca] leading-none">0{index + 1}</span>
        <span className="font-tech text-[11px] sm:text-xs tracking-widest uppercase text-neutral-500 leading-snug">{caption}</span>
      </figcaption>
      <p className="mt-2 px-1 flex items-center gap-2 font-tech text-[10px] tracking-[0.3em] uppercase text-neutral-400">
        <MoveHorizontal size={13} className="text-[#ff6b00]" /> {dragLabel}
      </p>
    </motion.figure>
  );
};

export const BeforeAfter = () => {
  const { t } = useLang();
  return (
    <section id="before-after" data-testid="before-after-section" className="py-24 sm:py-32 bg-[#f4f1ec] border-y border-[#e8e5e0]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <EditorialHeading kicker={t.beforeAfter.kicker} lines={t.beforeAfter.lines} size="text-4xl sm:text-5xl lg:text-6xl" />
        <p className="mt-5 text-sm text-neutral-500 max-w-md">{t.beforeAfter.sub}</p>
      </div>

      <div className="w-full px-5 sm:px-8 mt-14 grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-7">
        {t.beforeAfter.cases.map((c, i) => (
          <Slider
            key={i}
            index={i}
            pair={CASES[i]}
            beforeLabel={t.beforeAfter.before}
            afterLabel={t.beforeAfter.after}
            dragLabel={t.beforeAfter.drag}
            caption={c}
          />
        ))}
      </div>
    </section>
  );
};
